import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { getApiBaseUrl } from '../apiBase';

const API = getApiBaseUrl();

const RATE_UNIT_KEY = {
    hourly: 'rateUnitHourly', per_lesson: 'rateUnitPerLesson',
    monthly: 'rateUnitMonthly', percentage: 'rateUnitPercentage'
};
const RATE_LABEL_KEY = {
    hourly: 'rateHourly', per_lesson: 'ratePerLesson',
    monthly: 'rateMonthly', percentage: 'ratePercentage'
};
const TX_TYPE_KEY = {
    lesson_earned: 'typeLessonEarned', monthly_earned: 'typeMonthlyEarned',
    manual_earned: 'typeManualEarned', settled: 'typeSettled'
};

function money(v) {
    return (Number(v) || 0).toLocaleString('ru-RU');
}

export default function MyPayrollPage() {
    const { t } = useTranslation(['payroll', 'common']);
    const [school, setSchool] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const schoolRes = await getSchoolForSession(api);
                const schoolId = getSchoolIdFromResponse(schoolRes);
                if (cancelled) return;
                setSchool(schoolRes.data);
                const res = await api.get('/staff-payroll/my', { params: { schoolId } });
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.message || t('payroll:errLoad'));
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const card = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '14px' };

    if (loading) {
        return (
            <OwnerPageLayout title={t('payroll:myTitle')} maxWidth={900}>
                <div className="sp-loading-page">{t('payroll:loading')}</div>
            </OwnerPageLayout>
        );
    }

    if (err) {
        return (
            <OwnerPageLayout title={t('payroll:myTitle')} maxWidth={900}>
                <div style={{ padding: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b' }}>{err}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('payroll:myTitle')}
            subtitle={t('payroll:mySubtitle', { name: school?.name || '' })}
            maxWidth={900}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div className="sp-panel" style={card}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{t('payroll:myBalanceLabel')}</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: (data?.balance ?? 0) >= 0 ? '#0f172a' : '#b91c1c' }}>
                        {money(data?.balance)} ₽
                    </div>
                </div>
                <div className="sp-panel" style={card}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{t('payroll:myRateLabel')}</div>
                    {data?.rate ? (
                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                            {t(`payroll:${RATE_LABEL_KEY[data.rate.rateType]}`)}: {money(data.rate.amount)} {t(`payroll:${RATE_UNIT_KEY[data.rate.rateType]}`)}
                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginTop: 4 }}>
                                {t('payroll:rateSince', { date: data.rate.effectiveFrom })}
                            </div>
                        </div>
                    ) : <div style={{ color: '#94a3b8' }}>{t('payroll:myNoRate')}</div>}
                </div>
            </div>

            <div className="sp-panel" style={{ ...card, marginBottom: 14 }}>
                <h3 style={{ marginTop: 0 }}>{t('payroll:myRecentLessonsTitle')}</h3>
                {(data?.recentLessons || []).length === 0 && <div style={{ color: '#64748b' }}>{t('payroll:myRecentLessonsEmpty')}</div>}
                {(data?.recentLessons || []).length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colDate')}</th>
                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colTime')}</th>
                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colGroup')}</th>
                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colAmount')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.recentLessons || []).map((l, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 6px' }}>{l.date}</td>
                                        <td style={{ padding: '8px 6px' }}>{l.startTime}–{l.endTime}</td>
                                        <td style={{ padding: '8px 6px' }}>{l.groupName}</td>
                                        <td style={{ padding: '8px 6px', fontWeight: 700, color: '#059669' }}>+{money(l.earned)} ₽</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="sp-panel" style={card}>
                <h3 style={{ marginTop: 0 }}>{t('payroll:myHistoryTitle')}</h3>
                {(data?.history || []).length === 0 && <div style={{ color: '#64748b' }}>{t('payroll:myHistoryEmpty')}</div>}
                {(data?.history || []).length > 0 && (
                    <div style={{ display: 'grid', gap: 6 }}>
                        {(data.history || []).map(h => (
                            <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #f1f5f9', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
                                <div>
                                    <div>{t(`payroll:${TX_TYPE_KEY[h.type] || 'typeManualEarned'}`)}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(h.createdAt).toLocaleString('ru-RU')}{h.note ? ` · ${h.note}` : ''}</div>
                                </div>
                                <div style={{ fontWeight: 800, color: h.amount >= 0 ? '#059669' : '#b91c1c' }}>
                                    {h.amount >= 0 ? '+' : ''}{money(h.amount)} ₽
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </OwnerPageLayout>
    );
}
